import type { Transition, Variants } from "framer-motion";

/**
 * Shared motion presets for OT Desk. Kept here so pages stay DRY and the
 * visual language is consistent. The app does not gate on prefers-reduced-motion
 * — animations always play at full strength.
 */

export const easeOut: Transition["ease"] = [0.22, 1, 0.36, 1];

/** Slide + fade in from below. The default page/section entrance. */
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.34, ease: easeOut } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.18, ease: easeOut } },
};

/**
 * Page-level route crossfade — pure opacity, no slide. Route switches read as a
 * calm dissolve instead of fadeUp's quick slide, which felt "flashy" when the
 * outgoing page slid down while the incoming page slid up on top.
 */
export const pageFade: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
  exit: { opacity: 0, transition: { duration: 0.22, ease: [0.4, 0, 0.2, 1] } },
};

/** Pure fade — for overlays and full-bleed surfaces. */
export const fade: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.22, ease: easeOut } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

/** Pop/scale in — empty states and small badges. */
export const pop: Variants = {
  hidden: { opacity: 0, scale: 0.92 },
  show: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 320, damping: 30 } },
  exit: { opacity: 0, scale: 0.92, transition: { duration: 0.15 } },
};

/** Parent that staggers its children. Pair with fadeUp/pop on each child. */
export const stagger = (gap = 0.05, delay = 0): Variants => ({
  hidden: {},
  show: { transition: { staggerChildren: gap, delayChildren: delay } },
  exit: { transition: { staggerChildren: 0.03, staggerDirection: -1 } },
});

/**
 * Accordion expand/collapse: measures its own rendered height ("auto") so
 * following content glides instead of snapping when the section opens/closes.
 * Apply to an un-padded overflow-hidden wrapper around the real card so
 * card padding and margins collapse with it (border-box height can't go
 * below the element's own padding).
 */
export const collapse: Variants = {
  hidden: { opacity: 0, height: 0, overflow: "hidden" },
  show: { opacity: 1, height: "auto", overflow: "hidden", transition: { duration: 0.4, ease: easeOut } },
  exit: { opacity: 0, height: 0, overflow: "hidden", transition: { duration: 0.35, ease: easeOut } },
};

/**
 * List row that collapses to a true 0-height box. Padding/border must live on
 * an inner element (a border-box row can't collapse below its own padding).
 * Slower than `collapse` so rows glide open/closed instead of popping.
 */
export const listRow: Variants = {
  hidden: { opacity: 0, height: 0, marginBottom: 0, overflow: "hidden" },
  show: { opacity: 1, height: "auto", marginBottom: 0, overflow: "hidden", transition: { duration: 0.45, ease: easeOut } },
  exit: { opacity: 0, height: 0, marginBottom: 0, overflow: "hidden", transition: { duration: 0.35, ease: easeOut } },
};

/** Command palette backdrop. */
export const backdrop: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.18 } },
  exit: { opacity: 0, transition: { duration: 0.14 } },
};

/** Command palette / dropdown panel — drop + scale from the top. */
export const panel: Variants = {
  hidden: { opacity: 0, y: -16, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 380, damping: 34 } },
  exit: { opacity: 0, y: -10, scale: 0.98, transition: { duration: 0.16, ease: easeOut } },
};

/**
 * Top sheet that drops in with `panel` but closes by collapsing its own height
 * to zero (like `collapse`) — used by the End-of-day box so clicking close
 * makes it shrink away instead of scaling/fading.
 */
export const panelCollapse: Variants = {
  hidden: panel.hidden!,
  show: panel.show!,
  exit: collapse.exit!,
};

/** Shared layout spring for shared-element transitions (nav highlight, etc.). */
export const layoutSpring: Transition = { type: "spring", stiffness: 420, damping: 34 };

/** Standard tap feedback for buttons — use as whileTap/whileHover. */
export const tap = { whileHover: { scale: 1.02 }, whileTap: { scale: 0.97 } };